/* Evidence-chase grouping (D-3a), lifted out of TasksWorkbench (W6) so
 * the workbench's reusable queue component groups chases by the SAME
 * rules the old workbench used — one row per object, recommended
 * (non-blocking) chases behind a collapsed toggle.
 *
 * Chase producers mint one task per open obligation; a position with
 * three open codes lands three near-identical rows. The queue shows ONE
 * row per object ("3 evidence chases — <holder — instrument —
 * issuer>"), expandable to the individual obligations. Purely
 * display-side: the endpoint returns the full unpaginated queue, so
 * client grouping is always complete.
 */

import type { TaskQueueItem } from '../lib/tasks'

export function isEvidenceChase(t: TaskQueueItem): boolean {
  return t.task_type === 'investigation_action'
}

export function chaseIsRecommended(t: TaskQueueItem): boolean {
  const td = t.task_data as Record<string, unknown> | null
  if (td && typeof td === 'object' && 'is_mandatory' in td) return td.is_mandatory === false
  // Legacy gap tasks carry no is_mandatory in task_data — the producer
  // suffixes the title instead.
  return /\(recommended\)\s*$/.test(t.title || '')
}

/** Stable per-object key for a v2 chase task; legacy gap tasks (no object
 *  ids in task_data) stay ungrouped singles. */
export function chaseKeyOf(t: TaskQueueItem): string | null {
  if (!isEvidenceChase(t)) return null
  const td = t.task_data as Record<string, unknown> | null
  const ot = td?.object_type
  const oid = td?.object_id
  if (typeof ot === 'string' && typeof oid === 'string') return `${ot}:${oid}`
  return null
}

/** The object's officer-vocabulary name, parsed from the de-UUID'd producer
 *  title "Obtain evidence — <obligation (code)> — <object name>". The object
 *  name may itself contain em-dashes (holder — instrument — issuer), so take
 *  everything after the obligation segment. */
export function chaseObjectLabelOf(t: TaskQueueItem): string | null {
  const title = (t.title || '').replace(/\s*\(recommended\)\s*$/, '')
  const parts = title.split(' — ')
  if (parts.length >= 3 && parts[0].startsWith('Obtain evidence')) return parts.slice(2).join(' — ')
  return null
}

/** The obligation half of the chase title — the row label inside an expanded
 *  group ("Position balance (B1)"). */
export function chaseObligationLabelOf(t: TaskQueueItem): string {
  const title = (t.title || '').replace(/\s*\(recommended\)\s*$/, '')
  const parts = title.split(' — ')
  if (parts.length >= 2 && parts[0].startsWith('Obtain evidence')) return parts[1]
  return t.title
}
